import { getPhoto } from '../photoStore'

export interface PhotoViewerApi {
  open: (id: string, title: string) => void
  close: () => void
  isOpen: () => boolean
}

export function bindPhotoViewer(input: {
  onDelete: (id: string) => void
  onShare: (id: string) => void
}): PhotoViewerApi {
  const root = document.querySelector<HTMLElement>('#photo-viewer')!
  const image = document.querySelector<HTMLImageElement>('#pv-image')!
  const titleEl = document.querySelector<HTMLElement>('#pv-title')!
  const dateEl = document.querySelector<HTMLElement>('#pv-date')!
  const closeBtn = document.querySelector<HTMLButtonElement>('#pv-close')!
  const shareBtn = document.querySelector<HTMLButtonElement>('#pv-share')!
  const deleteBtn = document.querySelector<HTMLButtonElement>('#pv-delete')!

  let currentId: string | null = null
  let objectUrl: string | null = null
  let previousFocus: HTMLElement | null = null
  let openGeneration = 0

  const focusables = () =>
    [...root.querySelectorAll<HTMLElement>('button:not([hidden])')].filter(
      (el) => !el.hasAttribute('disabled'),
    )

  const revokeFull = () => {
    if (!objectUrl) return
    URL.revokeObjectURL(objectUrl)
    objectUrl = null
    image.removeAttribute('src')
  }

  const close = () => {
    if (root.classList.contains('hidden')) return
    root.classList.add('hidden')
    root.setAttribute('aria-hidden', 'true')
    root.inert = true
    currentId = null
    revokeFull()
    const restore = previousFocus
    previousFocus = null
    restore?.focus()
  }

  const open = (id: string, title: string) => {
    const generation = ++openGeneration
    currentId = id
    previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    titleEl.textContent = title
    dateEl.textContent = ''
    image.alt = title
    revokeFull()

    root.classList.remove('hidden')
    root.removeAttribute('aria-hidden')
    root.inert = false
    closeBtn.focus()

    void getPhoto(id).then((photo) => {
      if (generation !== openGeneration || currentId !== id) return
      if (!photo) {
        close()
        return
      }
      titleEl.textContent = photo.subject || title
      image.alt = photo.subject || title
      if (photo.takenAt) {
        const taken = new Date(photo.takenAt)
        dateEl.textContent = Number.isNaN(taken.getTime())
          ? ''
          : taken.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
      }
      objectUrl = URL.createObjectURL(photo.full)
      image.src = objectUrl
    })
  }

  closeBtn.addEventListener('click', (event) => {
    event.stopPropagation()
    close()
  })
  shareBtn.addEventListener('click', (event) => {
    event.stopPropagation()
    if (currentId) input.onShare(currentId)
  })
  deleteBtn.addEventListener('click', (event) => {
    event.stopPropagation()
    const id = currentId
    if (!id) return
    close()
    input.onDelete(id)
  })
  root.addEventListener('click', (event) => {
    if (event.target === root) close()
  })

  window.addEventListener(
    'keydown',
    (event) => {
      if (root.classList.contains('hidden')) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        close()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
    true,
  )

  close()
  return {
    open,
    close,
    isOpen: () => !root.classList.contains('hidden'),
  }
}
